import { Moment } from 'moment-timezone';
import React, { useCallback, useMemo } from 'react';
import { Select } from '../../../../Components/Form';
import { isMantraAdmin, useCurrentProvider } from '../../../../Components/Permissions';
import { TimeZone } from '../../../../utils';
import { LocalAllocation } from './Hooks/types';
import { hasTimeType, generateDropdownTimeSlots, getDateInTz } from './availabilityUtils';

export type TimeType = 'startTime' | 'endTime';

type TimeSelectProps<T extends TimeType> = {
  timeType: T;
  timezone: TimeZone;
  forDate: Moment;
  allocation: LocalAllocation;
  onChange: (timeType: T, updatedMoment: Moment) => void;
  style?: React.CSSProperties;
  className?: string;
  id?: string;
  disabled?: boolean;
};

// Using JS Date, destroys the timezone
export function TimeSelect<T extends TimeType>({
  timeType,
  timezone,
  forDate,
  allocation,
  onChange,
  style,
  className,
  id,
  disabled = false,
}: TimeSelectProps<T>) {
  const { currentProvider } = useCurrentProvider();
  const isAdmin = isMantraAdmin(currentProvider);

  const selectionStartMoment = hasTimeType(allocation, timeType)
    ? allocation[timeType].clone().tz(timezone)
    : null;

  const allocStartTime = hasTimeType(allocation, 'startTime') ? allocation.startTime : null;
  const baseDate = getDateInTz(forDate, timezone);

  const selectionOptions = useMemo(() => {
    return generateDropdownTimeSlots({
      timeType,
      isAdmin: isAdmin || disabled,
      forDate: baseDate,
      allocation,
    });
  }, [selectionStartMoment, forDate, allocStartTime, timeType, isAdmin, disabled]);

  const currentSelection = selectionStartMoment
    ? selectionOptions.findIndex(s => s.isSame(selectionStartMoment))
    : undefined;

  const convertBackToAllocTimezone = useCallback(
    (idx: number) => selectionOptions[idx].clone().tz(allocation.timezone),
    [selectionOptions, allocation.timezone]
  );

  return (
    <Select
      id={id}
      placeholder="Select"
      disabled={disabled}
      clearable={false}
      value={currentSelection}
      options={selectionOptions.map((s, i) => ({
        id: i,
        label: s.isAfter(baseDate.clone().endOf('day'))
          ? `${s.format('h:mm a')} (+1)`
          : s.format('h:mm a'),
      }))}
      onChange={e => onChange(timeType, convertBackToAllocTimezone(e as number))}
      style={style}
      className={className}
    />
  );
}
